import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { dramas, genres, siteInfo } from "@/mocks/dramas";
import RankingTabs from "@/pages/ranking/components/RankingTabs";
import CategoryFilter from "@/pages/ranking/components/CategoryFilter";
import RankingRow from "@/pages/ranking/components/RankingRow";
import RankingSidebar from "@/pages/ranking/components/RankingSidebar";

const TAB_TITLES: Record<string, string> = {
  all: "综合榜",
  hot: "热度榜",
  rating: "口碑榜",
  new: "新剧榜",
};

const TAB_DESC: Record<string, string> = {
  all: "综合剧情完成度、节奏与读者反馈给出的总排名，适合第一次来这里、想直接看最好那几部的观众。",
  hot: "按站内热度排序，反映近期真实的追剧趋势，节奏快、反转密的作品通常更靠前。",
  rating: "按编辑评分排序，更看重剧情的完整与耐看程度，适合偏爱慢热精品的观众。",
  new: "近期上线与正在更新的新剧优先展示，先收藏再慢慢追，避免错过刚起势的好剧。",
};

export default function Ranking() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") ?? "all";
  const category = searchParams.get("category") ?? "";
  const keyword = searchParams.get("q") ?? "";

  const activeGenre = genres.find((g) => g.slug === category);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const list = useMemo(() => {
    let result = [...dramas];

    if (activeGenre) {
      result = result.filter((d) => d.category === activeGenre.name);
    }

    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      result = result.filter(
        (d) =>
          d.title.toLowerCase().includes(kw) ||
          d.subtitle.toLowerCase().includes(kw) ||
          d.category.toLowerCase().includes(kw) ||
          d.tags.some((tag) => tag.toLowerCase().includes(kw))
      );
    }

    if (tab === "hot") result.sort((a, b) => b.heat - a.heat);
    else if (tab === "rating") result.sort((a, b) => b.rating - a.rating);
    else if (tab === "new")
      result.sort((a, b) => {
        if (a.status === b.status) return b.year - a.year || b.heat - a.heat;
        return a.status === "更新中" ? -1 : 1;
      });
    else result.sort((a, b) => b.rating * 0.6 + b.heat * 0.04 - (a.rating * 0.6 + a.heat * 0.04));

    return result;
  }, [activeGenre, keyword, tab]);

  const picks = useMemo(() => [...dramas].sort((a, b) => b.rating - a.rating).slice(0, 5), []);

  const heading = `${activeGenre ? `${activeGenre.name} · ` : ""}${TAB_TITLES[tab] ?? "综合榜"}`;

  const rankingJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${heading} - 黑料短剧排行榜`,
      description: TAB_DESC[tab],
      inLanguage: "zh-CN",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://heiliao-theater.cn/" },
          { "@type": "ListItem", position: 2, name: "排行榜", item: "https://heiliao-theater.cn/ranking" },
        ],
      },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: list.length,
        itemListElement: list.map((drama, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: drama.title,
          url: `https://heiliao-theater.cn/drama/${drama.id}`,
        })),
      },
    }),
    [heading, list, tab]
  );

  useSeo({
    title: `${heading} - 黑料短剧排行榜与热门短剧推荐 | ${siteInfo.name}`,
    description:
      "黑料短剧排行榜：综合榜、热度榜、口碑榜与新剧榜一键切换，支持按豪门恩怨、重生逆袭、悬疑复仇等口味筛选，快速找到同款类似的热门短剧。",
    keywords: "黑料短剧排行榜,黑料短剧热门短剧,黑料短剧新剧,黑料短剧推荐,短剧榜单一键筛选",
    path: "/ranking",
    jsonLd: rankingJsonLd,
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200/60 bg-background-100/50">
        <div className="mx-auto max-w-page px-4 pb-10 pt-28 md:px-6 md:pt-36">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "排行榜" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            黑料短剧排行榜
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600">
            {TAB_DESC[tab]}
          </p>

          <div className="mt-7 flex flex-col gap-5">
            <RankingTabs active={tab} onChange={(key) => updateParam("tab", key === "all" ? "" : key)} />
            <CategoryFilter active={category} onChange={(slug) => updateParam("category", slug)} />
          </div>
        </div>
      </div>

      <section className="py-12 md:py-16">
        <div className="mx-auto grid max-w-page grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{heading}</h2>
              <p className="text-xs font-label text-foreground-500">
                共 {list.length} 部作品{keyword.trim() ? ` · 关键词「${keyword.trim()}」` : ""}
              </p>
            </div>

            {list.length === 0 ? (
              <div className="mt-8 rounded-lg border border-background-200/70 bg-background-100/60 p-10 text-center">
                <i className="ri-emotion-unhappy-line text-3xl text-foreground-500" aria-hidden="true"></i>
                <p className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有找到匹配的短剧
                </p>
                <p className="mt-2 text-sm text-foreground-600">
                  换个关键词，或清空筛选条件再看看。
                </p>
                <button
                  type="button"
                  onClick={() => setSearchParams({}, { replace: true })}
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label text-background-950 transition-colors hover:bg-primary-400 cursor-pointer"
                >
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  清空筛选
                </button>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {list.map((drama, index) => (
                  <RankingRow
                    key={drama.id}
                    drama={drama}
                    rank={index + 1}
                    metricLabel={tab === "rating" ? "口碑分" : "热度"}
                    metricValue={tab === "rating" ? Math.round(drama.rating * 10) : drama.heat}
                  />
                ))}
              </div>
            )}
          </div>

          <RankingSidebar picks={picks} />
        </div>
      </section>
    </SiteLayout>
  );
}